/**
 * The one set of validation rules the React clients use.
 *
 * <p>These are deliberately the SAME rules, with the same limits and the same wording, as the
 * API's `KSS.Thalios.Library.Validation.ThaliosPatterns` / the Flutter app's
 * `lib/core/validation/validators.dart`. A message the user reads in the form is the message the
 * server would have replied with — the alternative (each layer inventing its own limits) is how a
 * form accepts a value the API then rejects with a toast that names no field.</p>
 *
 * A rule returns `null` when the value is acceptable, or a human-readable message when it is not.
 * Rules are plain functions of the value, so the same rule runs on typing, on blur, on paste and
 * on submit without three code paths that can disagree.
 */

/** A validation rule. `all` is the rest of the form, for cross-field rules (confirm password, From < To). */
export type Rule<T = string, F = Record<string, unknown>> = (value: T, all: F) => string | null;

/* ────────────────────────────────────────────────────────────────────────────────────────────
 * Patterns — kept character-for-character in step with ThaliosPatterns on the server.
 * ──────────────────────────────────────────────────────────────────────────────────────────── */

export const PATTERNS = {
  /** Indian mobile: 10 digits starting 6-9. */
  mobile: /^[6-9]\d{9}$/,
  digits: /^\d+$/,
  /** A decimal amount with at most two places — money, as printed. */
  money: /^\d{1,13}(\.\d{1,2})?$/,
  gstin: /^\d{2}[A-Z]{5}\d{4}[A-Z]\d[Z][A-Z\d]$/,
  pan: /^[A-Z]{5}\d{4}[A-Z]$/,
  pincode: /^[1-9]\d{5}$/,
  ifsc: /^[A-Z]{4}0[A-Z0-9]{6}$/,
  /** 24h "HH:mm". */
  time24: /^([01]\d|2[0-3]):[0-5]\d$/,
  /** 12h "hh:mm AM/PM". */
  time12: /^(0?[1-9]|1[0-2]):[0-5]\d\s?([AaPp][Mm])$/,
  /** Letters, digits, dash, slash, underscore — no quotes, angle brackets or semicolons. */
  code: /^[A-Za-z0-9\-/_]+$/,
  /** A person/business name: real names contain O'Brien, Smith & Sons, R.K. Traders, Café. */
  name: /^[\p{L}\p{N} .,'&()\-/]+$/u,
  /** Deliberately permissive but structural — one @, a dot-separated domain, no spaces. */
  email: /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)+$/,
  url: /^https?:\/\/[^\s/$.?#].[^\s]*$/i
} as const;

/**
 * SQL-injection signatures.
 *
 * <b>This is defence in depth, not the defence.</b> The real protection is parameterised EF Core
 * queries on the server. This exists to reject obviously hostile input early, at the field, where
 * the user can see which field is the problem.
 */
export const SQL_SIGNATURE =
  /(--|;|\/\*|\*\/|\bxp_\w+|\b(DROP|DELETE|TRUNCATE|ALTER|EXEC(UTE)?|UNION(\s+ALL)?\s+SELECT|INSERT\s+INTO|UPDATE\s+\w+\s+SET)\b)/i;

/** HTML/script payloads. React escapes on render; this stops hostile content being *stored*, which
 *  matters because stored text is later printed onto invoices and pushed to other clients. */
export const HTML_SIGNATURE =
  /(<\s*script|<\s*\/?\s*(iframe|object|embed|img|svg|a|div|span|style|link|meta)\b|javascript\s*:|on\w+\s*=)/i;

/* ────────────────────────────────────────────────────────────────────────────────────────────
 * Rule builders
 * ──────────────────────────────────────────────────────────────────────────────────────────── */

const str = (v: unknown): string => (v == null ? '' : String(v));
const blank = (v: unknown): boolean => str(v).trim().length === 0;

/** Required. Trims first, so a field of pure spaces fails as it should. */
export const required =
  (label = 'This field'): Rule<unknown> =>
  (v) =>
    blank(v) ? `${label} is required.` : null;

/** Required for a checkbox / consent toggle. */
export const requiredChecked =
  (label = 'This box'): Rule<boolean> =>
  (v) =>
    v === true ? null : `${label} must be ticked.`;

/** Required for a dropdown / radio group — an empty string or a placeholder value is "unselected". */
export const requiredSelect =
  (label = 'A value', placeholders: string[] = ['', 'select', 'select…', 'select...', '0', 'none']): Rule<unknown> =>
  (v) =>
    placeholders.includes(str(v).trim().toLowerCase()) ? `${label} must be selected.` : null;

export const minLength =
  (n: number, label = 'This field'): Rule<unknown> =>
  (v) =>
    !blank(v) && str(v).trim().length < n ? `${label} must be at least ${n} characters.` : null;

export const maxLength =
  (n: number, label = 'This field'): Rule<unknown> =>
  (v) =>
    str(v).trim().length > n ? `${label} can't be longer than ${n} characters.` : null;

/** Digits only — no sign, no separators, no decimal point. */
export const numeric =
  (label = 'This field'): Rule<unknown> =>
  (v) =>
    blank(v) || PATTERNS.digits.test(str(v).trim()) ? null : `${label} must be digits only.`;

/** A whole number, optionally bounded. */
export const integer =
  (label = 'This field', opts: { min?: number; max?: number } = {}): Rule<unknown> =>
  (v) => {
    if (blank(v)) return null;
    const s = str(v).trim();
    if (!/^-?\d+$/.test(s)) return `${label} must be a whole number.`;
    const n = Number(s);
    if (opts.min != null && n < opts.min) return `${label} can't be less than ${opts.min}.`;
    if (opts.max != null && n > opts.max) return `${label} can't be more than ${opts.max}.`;
    return null;
  };

/** A decimal, with a configurable number of places (default 2). */
export const decimal =
  (label = 'This field', opts: { places?: number; min?: number; max?: number } = {}): Rule<unknown> =>
  (v) => {
    if (blank(v)) return null;
    const s = str(v).trim();
    const places = opts.places ?? 2;
    if (!new RegExp(`^-?\\d+(\\.\\d{1,${places}})?$`).test(s))
      return `${label} must be a number with at most ${places} decimal place${places === 1 ? '' : 's'}.`;
    const n = Number(s);
    if (opts.min != null && n < opts.min) return `${label} can't be less than ${opts.min}.`;
    if (opts.max != null && n > opts.max) return `${label} can't be more than ${opts.max}.`;
    return null;
  };

/** Money: non-negative, at most two decimal places. */
export const currency =
  (label = 'Amount', opts: { min?: number; max?: number; allowZero?: boolean } = {}): Rule<unknown> =>
  (v) => {
    if (blank(v)) return null;
    const s = str(v).trim();
    if (!PATTERNS.money.test(s)) return `${label} must be an amount like 1250.00.`;
    const n = Number(s);
    if (!opts.allowZero && n === 0 && opts.min == null) return `${label} must be greater than zero.`;
    if (opts.min != null && n < opts.min) return `${label} can't be less than ${opts.min}.`;
    if (opts.max != null && n > opts.max) return `${label} can't be more than ${opts.max}.`;
    return null;
  };

export const email =
  (label = 'Email'): Rule<unknown> =>
  (v) =>
    blank(v) || PATTERNS.email.test(str(v).trim()) ? null : `Enter a valid ${label.toLowerCase()} address.`;

/**
 * Mobile number. Digits only, configurable length and leading digits so a tenant outside India
 * can be configured rather than patched.
 */
export const mobile =
  (
    label = 'Mobile number',
    opts: { length?: number; startsWith?: string; pattern?: RegExp } = {}
  ): Rule<unknown> =>
  (v) => {
    if (blank(v)) return null;
    const d = str(v).replace(/\D/g, '');
    const len = opts.length ?? 10;
    if (opts.pattern) return opts.pattern.test(d) ? null : `Enter a valid ${label.toLowerCase()}.`;
    if (d.length !== len) return `${label} must be exactly ${len} digits.`;
    const starts = opts.startsWith ?? '6789';
    if (starts && !starts.includes(d[0] ?? '')) return `${label} must start with ${starts.split('').join(', ')}.`;
    return null;
  };

export const url =
  (label = 'URL'): Rule<unknown> =>
  (v) =>
    blank(v) || PATTERNS.url.test(str(v).trim()) ? null : `${label} must start with http:// or https://.`;

export const pattern =
  (re: RegExp, message: string): Rule<unknown> =>
  (v) =>
    blank(v) || re.test(str(v).trim()) ? null : message;

export const gstin = (label = 'GSTIN'): Rule<unknown> =>
  pattern(PATTERNS.gstin, `${label} must be 15 characters, like 29ABCDE1234F1Z5.`);

export const pan = (label = 'PAN'): Rule<unknown> =>
  pattern(PATTERNS.pan, `${label} must be 10 characters, like ABCDE1234F.`);

export const pincode = (label = 'PIN code'): Rule<unknown> =>
  pattern(PATTERNS.pincode, `${label} must be 6 digits.`);

export const ifsc = (label = 'IFSC'): Rule<unknown> =>
  pattern(PATTERNS.ifsc, `${label} must be 11 characters, like HDFC0001234.`);

/** No leading/trailing spaces survive — this reports the ones a paste introduced. */
export const noEdgeSpaces =
  (label = 'This field'): Rule<unknown> =>
  (v) => {
    const s = str(v);
    return s.length > 0 && s !== s.trim() ? `${label} can't start or end with a space.` : null;
  };

/** Runs of consecutive spaces are almost always a paste artefact, and make two otherwise-identical
 *  names look different to a duplicate check. */
export const noDoubleSpace =
  (label = 'This field'): Rule<unknown> =>
  (v) =>
    str(v).includes('  ') ? `${label} can't contain double spaces.` : null;

/** Restrict to the characters a real name contains. */
export const safeName =
  (label = 'This field'): Rule<unknown> =>
  (v) =>
    blank(v) || PATTERNS.name.test(str(v).trim())
      ? null
      : `${label} contains characters that aren't allowed.`;

/** Restrict to a document/reference code shape. */
export const safeCode =
  (label = 'This field'): Rule<unknown> =>
  (v) =>
    blank(v) || PATTERNS.code.test(str(v).trim())
      ? null
      : `${label} may only contain letters, digits, - / and _.`;

export const noSqlInjection =
  (label = 'This field'): Rule<unknown> =>
  (v) =>
    SQL_SIGNATURE.test(str(v)) ? `${label} contains characters or keywords that aren't allowed.` : null;

export const noHtml =
  (label = 'This field'): Rule<unknown> =>
  (v) =>
    HTML_SIGNATURE.test(str(v)) ? `${label} can't contain HTML or scripts.` : null;

/** The pair applied to every free-text field. */
export const safeText = (label = 'This field'): Rule<unknown>[] => [noSqlInjection(label), noHtml(label)];

/* ── Dates ─────────────────────────────────────────────────────────────────────────────────── */

/** Parse dd-MM-yyyy, ISO yyyy-MM-dd, or dd/MM/yyyy to a Date; null when it isn't a real calendar date. */
export function parseDate(value: string): Date | null {
  const v = (value || '').trim();
  if (!v) return null;
  let m = /^(\d{2})[-/.](\d{2})[-/.](\d{4})$/.exec(v); // dd-MM-yyyy
  let year: number, mon: number, day: number;
  if (m) {
    day = +m[1]!;
    mon = +m[2]!;
    year = +m[3]!;
  } else {
    m = /^(\d{4})-(\d{2})-(\d{2})/.exec(v); // ISO
    if (!m) return null;
    year = +m[1]!;
    mon = +m[2]!;
    day = +m[3]!;
  }
  const dt = new Date(year, mon - 1, day);
  // Reject rollovers — 31-02-2026 would silently become 03-03.
  if (dt.getFullYear() !== year || dt.getMonth() !== mon - 1 || dt.getDate() !== day) return null;
  return dt;
}

/**
 * A real calendar date inside a sane window.
 *
 * The year bounds are the point: a plain `new Date(...)` happily accepts the 0202 / 20226 typo,
 * which then sorts to the beginning of time and quietly corrupts every date-ranged report.
 */
export const date =
  (
    label = 'Date',
    opts: { minYear?: number; maxYearsAhead?: number; notFuture?: boolean; notPast?: boolean; min?: Date; max?: Date } = {}
  ): Rule<unknown> =>
  (v) => {
    if (blank(v)) return null;
    const dt = parseDate(str(v));
    if (!dt) return `${label} must be a real date (dd-MM-yyyy).`;
    const minYear = opts.minYear ?? 2000;
    const maxYear = new Date().getFullYear() + (opts.maxYearsAhead ?? 5);
    if (dt.getFullYear() < minYear || dt.getFullYear() > maxYear)
      return `${label} must be between ${minYear} and ${maxYear}.`;
    if (opts.notFuture) {
      const end = new Date();
      end.setHours(23, 59, 59, 999); // today counts as valid; anything after does not
      if (dt.getTime() > end.getTime()) return `${label} can't be later than today.`;
    }
    if (opts.notPast) {
      const start = new Date();
      start.setHours(0, 0, 0, 0);
      if (dt.getTime() < start.getTime()) return `${label} can't be earlier than today.`;
    }
    if (opts.min && dt.getTime() < opts.min.getTime()) return `${label} is too early.`;
    if (opts.max && dt.getTime() > opts.max.getTime()) return `${label} is too late.`;
    return null;
  };

/** `to` must not be earlier than `from`. Attach to the "to" field, naming the "from" field's key. */
export const dateAfter =
  <F extends Record<string, unknown>>(fromKey: keyof F, label = 'To date', fromLabel = 'from date'): Rule<unknown, F> =>
  (v, all) => {
    if (blank(v)) return null;
    const to = parseDate(str(v));
    const from = parseDate(str(all?.[fromKey]));
    if (!to || !from) return null; // each field reports its own format error
    return to.getTime() < from.getTime() ? `${label} can't be earlier than the ${fromLabel}.` : null;
  };

/* ── Times ─────────────────────────────────────────────────────────────────────────────────── */

/** Minutes since midnight for "HH:mm" or "hh:mm AM/PM"; null when unparseable. */
export function parseTime(value: string): number | null {
  const v = (value || '').trim();
  if (!v) return null;
  const m12 = PATTERNS.time12.exec(v);
  if (m12) {
    let h = +m12[1]!;
    const min = +v.split(':')[1]!.slice(0, 2);
    const pm = /pm/i.test(m12[2]!);
    if (h === 12) h = 0;
    return (pm ? h + 12 : h) * 60 + min;
  }
  const m24 = PATTERNS.time24.exec(v);
  if (m24) return +v.slice(0, 2) * 60 + +v.slice(3, 5);
  return null;
}

export const time =
  (label = 'Time', opts: { format?: '24' | '12' | 'any' } = {}): Rule<unknown> =>
  (v) => {
    if (blank(v)) return null;
    const s = str(v).trim();
    const fmt = opts.format ?? 'any';
    if (fmt === '24') return PATTERNS.time24.test(s) ? null : `${label} must be in 24-hour HH:mm format.`;
    if (fmt === '12') return PATTERNS.time12.test(s) ? null : `${label} must look like 09:30 AM.`;
    return parseTime(s) == null ? `${label} must be a valid time.` : null;
  };

/** `to` must be strictly later than `from`. Attach to the "to" field. */
export const timeAfter =
  <F extends Record<string, unknown>>(fromKey: keyof F, label = 'To time', fromLabel = 'from time'): Rule<unknown, F> =>
  (v, all) => {
    if (blank(v)) return null;
    const to = parseTime(str(v));
    const from = parseTime(str(all?.[fromKey]));
    if (to == null || from == null) return null;
    return to <= from ? `${label} must be later than the ${fromLabel}.` : null;
  };

/* ── Passwords ─────────────────────────────────────────────────────────────────────────────── */

/**
 * Password strength. The defaults match the API's own rule so a password accepted here is a
 * password the server will accept.
 */
export const password =
  (
    label = 'Password',
    opts: { min?: number; max?: number; requireUpper?: boolean; requireDigit?: boolean; requireSymbol?: boolean } = {}
  ): Rule<unknown> =>
  (v) => {
    if (blank(v)) return null;
    const s = str(v);
    const min = opts.min ?? 8;
    const max = opts.max ?? 64;
    if (s.length < min) return `${label} must be at least ${min} characters.`;
    if (s.length > max) return `${label} can't be longer than ${max} characters.`;
    if (opts.requireUpper !== false && !/[A-Z]/.test(s)) return `${label} needs an uppercase letter.`;
    if (opts.requireDigit !== false && !/\d/.test(s)) return `${label} needs a number.`;
    if (opts.requireSymbol && !/[^A-Za-z0-9]/.test(s)) return `${label} needs a symbol.`;
    return null;
  };

/** Attach to the confirm field, naming the password field's key. */
export const matches =
  <F extends Record<string, unknown>>(otherKey: keyof F, message = "Passwords don't match."): Rule<unknown, F> =>
  (v, all) =>
    str(v) === str(all?.[otherKey]) ? null : message;

/** A numeric PIN of a fixed length (staff POS login). */
export const pin =
  (length = 4, label = 'PIN'): Rule<unknown> =>
  (v) => {
    if (blank(v)) return null;
    const s = str(v).trim();
    return new RegExp(`^\\d{${length}}$`).test(s) ? null : `${label} must be ${length} digits.`;
  };

/* ── Files ─────────────────────────────────────────────────────────────────────────────────── */

/**
 * File type + size. `accept` is a list of extensions (".pdf") and/or MIME types ("image/png",
 * "image/*"), matching what the `accept` attribute already declares on the input.
 */
export function validateFile(
  file: File | null | undefined,
  opts: { accept?: string[]; maxMb?: number; label?: string } = {}
): string | null {
  if (!file) return null;
  const label = opts.label ?? 'File';
  if (opts.accept?.length) {
    const name = file.name.toLowerCase();
    const type = (file.type || '').toLowerCase();
    const ok = opts.accept.some((a) => {
      const rule = a.trim().toLowerCase();
      if (rule.startsWith('.')) return name.endsWith(rule);
      if (rule.endsWith('/*')) return type.startsWith(rule.slice(0, -1));
      return type === rule;
    });
    if (!ok) return `${label} must be a ${opts.accept.join(', ')} file.`;
  }
  if (opts.maxMb != null && file.size > opts.maxMb * 1024 * 1024)
    return `${label} must be under ${opts.maxMb} MB.`;
  if (file.size === 0) return `${label} is empty.`;
  return null;
}

/* ── Duplicates ────────────────────────────────────────────────────────────────────────────── */

/**
 * Reject a value that already exists. Comparison is trimmed, case- and whitespace-insensitive,
 * because "Acme  Ltd" and "acme ltd" are the duplicate the user meant to avoid.
 */
export const unique =
  (existing: () => string[], label = 'This value'): Rule<unknown> =>
  (v) => {
    if (blank(v)) return null;
    const norm = (s: string) => s.trim().toLowerCase().replace(/\s+/g, ' ');
    const target = norm(str(v));
    return existing().some((e) => norm(e) === target) ? `${label} already exists.` : null;
  };

/* ── Composition ───────────────────────────────────────────────────────────────────────────── */

/** Run rules in order and return the FIRST message, so a field shows one clear problem at a time. */
export function runRules<T, F extends Record<string, unknown>>(
  value: T,
  all: F,
  rules: readonly Rule<T, F>[] | undefined
): string | null {
  if (!rules) return null;
  for (const rule of rules) {
    const message = rule(value, all);
    if (message) return message;
  }
  return null;
}
